import {
	addDays,
	differenceInCalendarDays,
	format,
	isSameDay,
	isToday,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { DateRange as DayPickerDateRange } from "react-day-picker";
import { Button } from "@/v2/components/ui/button";
import { Calendar } from "@/v2/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import { type DateRange, formatDateRange } from "@/v2/lib/dates";
import { cn } from "@/v2/lib/utils";

export type { DateRange } from "@/v2/lib/dates";
export { DatePickerDefaultFooter } from "./DatePickerDefaultFooter";

export interface DatePickerSlotProps {
	selectDate: (date: Date) => void;
	selectRange: (range: DateRange) => void;
	close: () => void;
}

type DatePickerProps = {
	/** DatePicker fills this container; use this to constrain width at call sites. */
	className?: string;
	/** Optional side slot rendered beside the calendar. Omit/null to render nothing. */
	sideSlot?: ((props: DatePickerSlotProps) => ReactNode) | null;
	/** Optional bottom slot rendered below the calendar. Omit/null to render nothing. */
	bottomSlot?: ((props: DatePickerSlotProps) => ReactNode) | null;
	/** Optional className for the visible trigger button. */
	triggerClassName?: string;
	onDateChange: (date: Date) => void;
} & (
	| {
			selectionMode: "single";
			date: Date;
			dateRange?: never;
			onDateRangeChange?: never;
	  }
	| {
			selectionMode: "range";
			dateRange: DateRange;
			date?: never;
			onDateRangeChange: (range: DateRange) => void;
	  }
);

export function DatePicker(props: DatePickerProps) {
	const {
		className,
		onDateChange,
		sideSlot = null,
		bottomSlot = null,
		triggerClassName,
		selectionMode,
	} = props;
	const selectedDate =
		props.selectionMode === "single" ? props.date : undefined;
	const selectedRange =
		props.selectionMode === "range" ? props.dateRange : undefined;
	const onDateRangeChange =
		props.selectionMode === "range" ? props.onDateRangeChange : undefined;
	const [open, setOpen] = useState(false);
	const [month, setMonth] = useState(
		selectedRange?.from ?? selectedDate ?? new Date(),
	);
	const [pending, setPending] = useState<DayPickerDateRange | undefined>(
		undefined,
	);

	const activeRange =
		selectedRange !== undefined &&
		!isSameDay(selectedRange.from, selectedRange.to)
			? selectedRange
			: null;

	const displayDate =
		selectedDate ??
		(selectedRange && isSameDay(selectedRange.from, selectedRange.to)
			? selectedRange.from
			: undefined);

	const handleOpenChange = (nextOpen: boolean) => {
		setOpen(nextOpen);
		if (nextOpen) {
			setMonth(activeRange?.from ?? displayDate ?? new Date());
			setPending(
				activeRange
					? { from: activeRange.from, to: activeRange.to }
					: displayDate
						? { from: displayDate, to: displayDate }
						: undefined,
			);
		}
	};

	const commitSingle = (d: Date) => {
		onDateChange(d);
		setOpen(false);
	};

	const commitRange = (from: Date, to: Date) => {
		if (isSameDay(from, to)) {
			commitSingle(from);
			return;
		}
		if (!onDateRangeChange) return;
		onDateRangeChange({ from, to });
		setOpen(false);
	};

	const shiftSelection = (direction: -1 | 1) => {
		if (!selectedRange) return;
		const span =
			Math.abs(differenceInCalendarDays(selectedRange.to, selectedRange.from)) +
			1;
		const offset = direction * span;
		commitRange(
			addDays(selectedRange.from, offset),
			addDays(selectedRange.to, offset),
		);
	};

	// 1st click = start, 2nd click = end, 3rd click starts over.
	// Clicks are ordered so the range is always valid; same day twice = a 1-day range.
	const handleRangeSelect = (
		_selected: DayPickerDateRange | undefined,
		clicked?: Date,
	) => {
		if (!clicked) return;
		const picking = Boolean(pending?.from && !pending?.to);
		if (!picking) {
			setPending({ from: clicked, to: undefined });
			return;
		}
		if (!pending?.from) return;
		const start = pending.from;
		const from = clicked < start ? clicked : start;
		const to = clicked < start ? start : clicked;
		setPending({ from, to });
		commitRange(from, to);
	};

	const slotProps: DatePickerSlotProps = {
		selectDate: (selectedDate) => {
			if (selectionMode === "single") commitSingle(selectedDate);
			else commitRange(selectedDate, selectedDate);
		},
		selectRange: ({ from, to }) => commitRange(from, to),
		close: () => setOpen(false),
	};
	const sideSlotContent = sideSlot?.(slotProps);
	const bottomSlotContent = bottomSlot?.(slotProps);

	const dateLabel = activeRange
		? formatDateRange(activeRange)
		: displayDate
			? isToday(displayDate)
				? "Today"
				: format(displayDate, "EEE, MMM d")
			: "Today";

	const calendar =
		selectionMode === "single" ? (
			<Calendar
				mode="single"
				month={month}
				onMonthChange={setMonth}
				selected={displayDate}
				onSelect={(selected) => {
					if (selected) commitSingle(selected);
				}}
				disabled={{ after: new Date() }}
			/>
		) : (
			<Calendar
				mode="range"
				month={month}
				onMonthChange={setMonth}
				selected={pending}
				onSelect={handleRangeSelect}
				disabled={{ after: new Date() }}
			/>
		);

	const rangeArrowButton = (direction: -1 | 1) => (
		<Button
			type="button"
			variant="outline"
			size="icon"
			className={cn(
				"h-10 w-10 shrink-0",
				direction === -1 ? "rounded-r-none" : "-ml-px rounded-l-none",
				triggerClassName,
			)}
			aria-label={direction === -1 ? "Previous date range" : "Next date range"}
			onPointerDown={(event) => event.stopPropagation()}
			onClick={(event) => {
				event.stopPropagation();
				shiftSelection(direction);
			}}
		>
			{direction === -1 ? (
				<ChevronLeftIcon className="size-4" />
			) : (
				<ChevronRightIcon className="size-4" />
			)}
		</Button>
	);

	return (
		<div className={cn("flex w-full items-center", className)}>
			{selectionMode === "range" ? (
				<div className="flex w-full min-w-0 items-center">
					{rangeArrowButton(-1)}
					<Popover open={open} onOpenChange={handleOpenChange}>
						<PopoverTrigger asChild>
							<Button
								type="button"
								variant="outline"
								className={cn(
									"-ml-px h-10 min-w-0 w-28 grow rounded-none px-3 text-base font-normal sm:w-32",
									triggerClassName,
								)}
								onPointerDown={(event) => event.stopPropagation()}
								onClick={(event) => event.stopPropagation()}
							>
								<span className="truncate">{dateLabel}</span>
							</Button>
						</PopoverTrigger>
						<PopoverContent
							className="w-auto p-0 overflow-hidden"
							align="center"
							onOpenAutoFocus={(event) => event.preventDefault()}
						>
							{sideSlotContent ? (
								<div className="flex">
									{sideSlotContent}
									<div className="p-2">{calendar}</div>
								</div>
							) : (
								calendar
							)}

							{bottomSlotContent}
						</PopoverContent>
					</Popover>
					{rangeArrowButton(1)}
				</div>
			) : (
				<Popover open={open} onOpenChange={handleOpenChange}>
					<PopoverTrigger asChild>
						<Button
							type="button"
							variant="outline"
							className={cn(
								"h-10 min-w-0 flex-1 justify-center gap-2 px-3 text-base font-normal",
								triggerClassName,
							)}
							onPointerDown={(event) => event.stopPropagation()}
							onClick={(event) => event.stopPropagation()}
						>
							<span>{dateLabel}</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent
						className="w-auto p-0 overflow-hidden"
						align="center"
						onOpenAutoFocus={(event) => event.preventDefault()}
					>
						{sideSlotContent ? (
							<div className="flex">
								{sideSlotContent}
								<div className="p-2">{calendar}</div>
							</div>
						) : (
							calendar
						)}

						{bottomSlotContent}
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
}
